// Registers the service worker (pwa/sw.js, written to dist/sw.js by the build) in production builds only, and tells
// the page when a newer version has installed and is waiting, so it can offer "tap to reload". Ignored, the new
// version takes over the next time the app is opened.

let waiting: ServiceWorker | null = null;
let reloading = false;
const subs = new Set<() => void>();
const emit = () => subs.forEach((f) => f());

export const updateWaiting = () => waiting !== null;
export function subscribeUpdate(f: () => void): () => void {
  subs.add(f);
  return () => subs.delete(f);
}

const SWITCHED_KEY = "cao.sw.switchedAt";
/** Whether this tab switched to a new version in the last minute (sessionStorage; false when it can't be read). */
function recentlySwitched(): boolean {
  try {
    return Date.now() - Number(sessionStorage.getItem(SWITCHED_KEY) ?? 0) < 60_000;
  } catch {
    return true; // can't remember: don't risk a loop
  }
}
function markSwitched(): void {
  try {
    sessionStorage.setItem(SWITCHED_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
}

/** Activate the waiting version and reload into it. */
export function applyUpdate(): void {
  if (!waiting) return;
  reloading = true;
  waiting.postMessage({ type: "SKIP_WAITING" });
}

export function registerServiceWorker(): void {
  // dev (`vite`) and the mock demo build never register one
  if (!import.meta.env.PROD || import.meta.env.VITE_MOCK === "1") return;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  const sw = navigator.serviceWorker;

  // only a version that replaces a running one is "new": the first install needs no reload
  const offer = (w: ServiceWorker | null) => {
    if (!w || !sw.controller) return;
    waiting = w;
    emit();
  };
  sw.addEventListener("controllerchange", () => {
    if (reloading) window.location.reload();
  });

  const start = async () => {
    try {
      const reg = await sw.register("/sw.js", { scope: "/" });
      offer(reg.waiting);
      // A version that was already waiting when this page loaded: a reload doesn't activate it (the tab stays a
      // client), so it would wait until every tab closed and the toast would show on every load. The page has only
      // just opened, so nothing is lost: switch now (once; a guard stops any reload loop).
      if (waiting && !recentlySwitched()) {
        markSwitched();
        applyUpdate();
      }
      reg.addEventListener("updatefound", () => {
        const next = reg.installing;
        next?.addEventListener("statechange", () => next.state === "installed" && offer(reg.waiting ?? next));
      });
      // a phone app stays open for days: look for a new version whenever it comes back to the front
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") reg.update().catch(() => undefined);
      });
    } catch {
      /* no service worker (blocked, private mode): the app works the same without one */
    }
  };
  if (document.readyState === "complete") void start();
  else window.addEventListener("load", () => void start(), { once: true });
}
